import Head from "next/head"
import { useRouter } from "next/router"
import { type PropsWithChildren } from "react"
import { Banner, Container } from "@octopus-energy/coral/atoms"
import { useCoralConfig } from "@octopus-energy/coral/providers"
import { coralBloks } from "@octopus-energy/coral/storyblok"
import { render } from "storyblok-rich-text-react-renderer-ts"
import { PageLoader } from "@/components/organisms"
import { AppLayout } from "@/features/navigation"
import { initStoryblok } from "../lib/initStoryblok"
import { type SbGlobalBannerStory, type SbStoryReturnType } from "../types"
import { getDefaultStringResolver } from "../utils/getDefaultStringResolver"
import { getMarkResolvers } from "../utils/getMarkResolvers"
import { getNodeResolvers } from "../utils/getNodeResolvers"
import { components } from "./index"

export type StoryblokLayoutProps = PropsWithChildren<{
    css: string | null
    globalBanner?: SbGlobalBannerStory
    story?: SbStoryReturnType
}>

initStoryblok({
    ...components,
    ...coralBloks,
})

export function StoryblokLayout({ children, globalBanner, story, css }: StoryblokLayoutProps) {
    const router = useRouter()
    const { headerHeight } = useCoralConfig()

    if (router.isFallback) {
        return <PageLoader />
    }

    return (
        <>
            {css && (
                <Head>
                    <style>{css}</style>
                </Head>
            )}

            <AppLayout isNavless={!!story?.content.isNavless}>
                {globalBanner?.content?.content && (
                    <Container
                        position="sticky"
                        top={headerHeight ?? 0}
                        zIndex={2}
                    >
                        <Banner>
                            {render(globalBanner.content.content, {
                                nodeResolvers: getNodeResolvers(),
                                markResolvers: getMarkResolvers("inherit"),
                                defaultStringResolver: getDefaultStringResolver,
                            })}
                        </Banner>
                    </Container>
                )}

                {children}
            </AppLayout>
        </>
    )
}

StoryblokLayout.messages = [...AppLayout.messages] as const
